import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";

export const IntroGate = ({ onEnter }) => {
    const videoRef = useRef(null);
    const [muted, setMuted] = useState(true);
    const [needsTap, setNeedsTap] = useState(false);

    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        // iOS Safari requires the muted/playsinline ATTRIBUTES present, not just properties
        v.setAttribute("muted", "");
        v.setAttribute("playsinline", "");
        v.setAttribute("webkit-playsinline", "");
        v.defaultMuted = true;
        v.muted = true;
        const tryPlay = () => v.play().then(() => setNeedsTap(false)).catch(() => setNeedsTap(true));
        tryPlay();
        v.addEventListener("canplay", tryPlay);
        v.addEventListener("loadeddata", tryPlay);
        // iOS fallback: any first touch anywhere starts playback (counts as a user gesture)
        const onFirstTouch = () => {
            if (v.paused) tryPlay();
        };
        window.addEventListener("touchstart", onFirstTouch, { once: true });
        window.addEventListener("click", onFirstTouch, { once: true });
        const watchdog = setTimeout(() => {
            if (v.paused) setNeedsTap(true);
        }, 2000);
        return () => {
            v.removeEventListener("canplay", tryPlay);
            v.removeEventListener("loadeddata", tryPlay);
            window.removeEventListener("touchstart", onFirstTouch);
            window.removeEventListener("click", onFirstTouch);
            clearTimeout(watchdog);
        };
    }, []);

    const handleGateTap = () => {
        const v = videoRef.current;
        if (v && v.paused) {
            v.play().then(() => setNeedsTap(false)).catch(() => {});
        }
    };

    const toggleSound = () => {
        const v = videoRef.current;
        if (!v) return;
        v.muted = !v.muted;
        if (!v.muted) v.volume = 0.85;
        setMuted(v.muted);
    };

    return (
        <motion.div
            data-testid="intro-gate"
            exit={{ opacity: 0, scale: 1.06 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-[200] bg-black"
            onClick={handleGateTap}
        >
            <video
                ref={videoRef}
                data-testid="intro-video"
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                className="absolute inset-0 h-full w-full object-cover bg-black"
            >
                <source src="/videos/opening-title.mp4" type="video/mp4" />
                <source src="/videos/opening-title.webm" type="video/webm" />
            </video>
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40" />

            <button
                data-testid="intro-sound-toggle"
                onClick={toggleSound}
                aria-label={muted ? "Turn sound on" : "Mute"}
                className="absolute top-6 right-6 flex h-11 w-11 items-center justify-center border border-white/25 text-white transition-colors duration-300 hover:border-[#d6c68e] hover:text-[#d6c68e]"
            >
                {muted ? <VolumeX size={17} /> : <Volume2 size={17} />}
            </button>

            <div className="absolute inset-x-0 bottom-0 flex flex-col items-center gap-7 pb-14 sm:pb-16">
                {needsTap && (
                    <motion.p
                        data-testid="intro-tap-to-play"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="font-mono text-[10px] tracking-[0.4em] text-[#d6c68e] animate-pulse"
                    >
                        TAP TO PLAY
                    </motion.p>
                )}
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.2, duration: 1.2 }}
                    className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.45em] text-zinc-400"
                >
                    Celestial Light Studios Presents
                </motion.p>
                <motion.button
                    data-testid="intro-enter-btn"
                    onClick={onEnter}
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.8, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    className="group relative border border-white/30 px-12 py-4 font-mono text-[12px] tracking-[0.5em] text-white transition-all duration-500 hover:border-[#d6c68e] hover:bg-[#d6c68e] hover:text-[#04060d] hover:shadow-[0_0_50px_rgba(214,198,142,0.55)]"
                >
                    ENTER
                </motion.button>
            </div>
        </motion.div>
    );
};
